import {
  getBackendLanguage,
  getTranslations,
  translateBackend,
} from '@assets/i18n/config';
import type { PortfolioTemplate } from '@interfaces/financialProduct';
import { Strategy } from '@interfaces/financialProduct';
import type { TranslatedString } from '@interfaces/shared';

import { Card, CardContent } from '@ovComponents/2-component/card/card';
import { ImportCsvModal } from '@ovComponents/2-component/importCsvModal/importCsvModal';
import InfoDialog from '@ovComponents/2-component/infoDialog/infoDialog';
import { ConfirmationModal } from '@ovComponents/3-pattern/confirmationDialog/confirmationDialog';
import { PieChartSecurities } from '@ovComponents/3-pattern/pieChartSecurities/pieChartSecurities';
import SecuritiesSelectionTable from '@ovComponents/3-pattern/securitiesSelectionTable/securitiesSelectionTable';
import type { Security } from '@ovComponents/3-pattern/securitiesSelectionTable/types';
import { additionalInfo } from '@ovComponents/4-module/configurableOptionFields/additionalInfo';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from 'hephaestus/web';
import {
  InfoOutlined as InfoOutlinedIcon,
  UploadFileRounded as UploadFileRoundedIcon,
} from 'hephaestus/webIcons';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ModelPortfolioData } from '../createModelPortfolio/createModelPortfolio.types';
import {
  computeAutoTarget,
  getTaxRankValidation,
  useAssetClassGrouping,
  validateGroupAllocations,
} from '../utils/useAssetClassGrouping';
import { ImportCsvPromptDialog } from './components/importCsvPromptDialog';
import { SecuritiesReviewDialog } from './components/securitiesReviewDialog';
import { ValidateSecuritiesDialogBox } from './components/validateSecuritiesDialogBox';

export const AddModelPortfolioSecuritiesVisual = ({
  options,
  workflowCompletion,
  securities = [],
  continueFunc,
  onAddSecurity,
  onUpdateSecurity,
  onUpdateTaxRank,
  onUpdateTargetPercentage,
  onRemoveSecurity,
  onSaveForLater,
  onImportFromCsv,
  onReorderSecurities,
  onAddCash,
  modelPortfolioDetails,
  loading,
  importLoading = false,
  showImportPrompt = false,
  onCloseImportPrompt,
  showReviewDialog = false,
  unrecognizedSecurities = [],
  availableSecurities = [],
  onReviewSubmit,
  onReviewClose,
}: {
  options: any;
  workflowCompletion?: any;
  securities: Security[];
  continueFunc: () => void;
  onAddSecurity: () => void;
  onUpdateSecurity: (index: number, security: Security) => void;
  onRemoveSecurity: (index: number) => void;
  onUpdateTaxRank: (index: number, value: number) => void;
  onUpdateTargetPercentage: (index: number, value: number) => void;
  onSaveForLater: () => void;
  onImportFromCsv: (file: File) => Promise<void>;
  onReorderSecurities: (securities: Security[]) => void;
  onAddCash?: () => void;
  modelPortfolioDetails: ModelPortfolioData;
  loading: boolean;
  importLoading?: boolean;
  showImportPrompt?: boolean;
  onCloseImportPrompt?: () => void;
  showReviewDialog?: boolean;
  unrecognizedSecurities?: Array<{
    ticker: string;
    targetWeight: number;
    rowIndex: number;
  }>;
  availableSecurities?: Array<{ id: string; ticker: string; name: string }>;
  onReviewSubmit?: (replacements: Record<string, string>) => void;
  onReviewClose?: () => void;
}) => {
  const { t } = useTranslation(['modelPortfolios', 'shared', 'buildModels']);
  const { sendAnalytic } = useContext(AnalyticsContext);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAllocationExceededDialogOpen, setIsAllocationExceededDialogOpen] =
    useState(false);
  const [isTemplateValidationDialogOpen, setIsTemplateValidationDialogOpen] =
    useState(false);
  const [utilizeRemainingAllocation, setUtilizeRemainingAllocation] =
    useState(false);

  const { portfolioTemplate } = useMemo(
    () => modelPortfolioDetails,
    [modelPortfolioDetails],
  );
  const [isEditingSubmitted, setIsEditingSubmitted] = useState(false);
  const [showImportCsvModal, setShowImportCsvModal] = useState(false);

  const getRoundedTotalPercentage = useCallback(
    () =>
      Number(
        securities
          .reduce((sum, security) => sum + (security.percentage || 0), 0)
          .toFixed(2),
      ),
    [securities],
  );

  const validate = () => {
    setIsEditingSubmitted(true);
    if (isTotalPercentageInvalid) {
      setIsAllocationExceededDialogOpen(true);
      return false;
    }
    if (
      (hasNoSecurity && holdingsExpected) ||
      hasNoPercentage ||
      invalidPrimaryAssetClass ||
      hasDuplicateTaxRanks ||
      hasGapsOrInvalidStart
    ) {
      return false;
    }
    const fields = [];
    if (
      options?.showSecurityAllocationTable?.required ===
        FieldRequiredType.ERROR &&
      securities.length === 0
    ) {
      fields.push('securities');
    }
    return fields.length === 0;
  };

  const checkTemplateCompliance = () => {
    if (!portfolioTemplate?.assetClassBranches) {
      return true;
    }

    const securitiesAssetClassMap = securities.reduce(
      (acc: Record<string, number>, security) => {
        const assetClass: string =
          security.financialProduct.primaryAssetClass?.translatedName?.[
            getBackendLanguage()
          ] ||
          security.financialProduct.primaryAssetClass?.name ||
          t('buildModels:other');
        acc[assetClass] = (acc[assetClass] || 0) + (security.percentage || 0);
        return acc;
      },
      {},
    );

    const hasMismatch = portfolioTemplate.assetClassBranches.some((branch) => {
      const assetClassName: string =
        branch.assetClass?.name || t('buildModels:other');
      const targetPercentage = branch.targetPercentage || 0;
      const actualPercentage = securitiesAssetClassMap[assetClassName] || 0;

      return Math.abs(targetPercentage - actualPercentage) > 0.01;
    });

    return !hasMismatch;
  };

  const submit = async () => {
    // A step configured with no table and nothing staged in it has nothing to
    // check: every question below is about holdings the advisor was given no
    // way to add, and asking them leaves the step with no way out.
    if (!tableShown && hasNoSecurity) {
      handleContinue();
      return;
    }

    if (isAssetClass) {
      const isValid = validateAssetClass();
      if (!isValid) return;
      setIsEditingSubmitted(false);
      handleContinue();
      return;
    }

    if (validate()) {
      const totalPercentage = getRoundedTotalPercentage();
      if (totalPercentage !== 100) {
        setIsDialogOpen(true);
        return;
      }

      if (!checkTemplateCompliance()) {
        setIsTemplateValidationDialogOpen(true);
        return;
      }

      setIsEditingSubmitted(false);
      handleContinue();
    }
  };

  const adjustSecuritiesForCashAndEquivalents = () => {
    if (!utilizeRemainingAllocation) {
      return;
    }

    const totalPercentage = getRoundedTotalPercentage();
    const remainingPercentage = 100 - totalPercentage;

    if (remainingPercentage <= 0) {
      return;
    }

    if (modelPortfolioDetails.zeroPercentCash) {
      const cashEquivalentSecurity = securities.find(
        (security) => security.isCashEquivalent,
      );
      if (cashEquivalentSecurity) {
        const updatedSecurities = securities.map((security) => {
          if (security.isCashEquivalent) {
            return {
              ...security,
              percentage: (security.percentage || 0) + remainingPercentage,
            };
          }
          return security;
        });
        onReorderSecurities(updatedSecurities);
      }
    } else {
      const existingCashSecurity = securities.find(
        (security) => security.financialProduct.isCash,
      );

      if (existingCashSecurity) {
        const updatedSecurities = securities.map((security) => {
          if (security.financialProduct.isCash) {
            return {
              ...security,
              percentage: (security.percentage || 0) + remainingPercentage,
            };
          }
          return security;
        });
        onReorderSecurities(updatedSecurities);
      } else {
        const cashLabel = t('buildModels:cash');
        const seenAssetClasses = new Set<string>();
        const orderedAssetClasses = securities.reduce<string[]>((acc, sec) => {
          const assetClass =
            sec.financialProduct.primaryAssetClass?.translatedName?.[
              getBackendLanguage()
            ] || sec.financialProduct.primaryAssetClass?.name;
          if (assetClass && !seenAssetClasses.has(assetClass)) {
            seenAssetClasses.add(assetClass);
            acc.push(assetClass);
          }
          return acc;
        }, []);

        if (!seenAssetClasses.has(cashLabel)) {
          orderedAssetClasses.push(cashLabel);
        }

        const colorList = [
          'supportOne',
          'supportTwo',
          'supportThree',
          'supportFour',
          'supportFive',
          'supportSix',
        ];
        const newColorMap = orderedAssetClasses.reduce<
          Record<
            string,
            | 'supportOne'
            | 'supportTwo'
            | 'supportThree'
            | 'supportFour'
            | 'supportFive'
            | 'supportSix'
          >
        >(
          (acc, assetClass, i) => ({
            ...acc,
            [assetClass]: colorList[i % colorList.length] as
              | 'supportOne'
              | 'supportTwo'
              | 'supportThree'
              | 'supportFour'
              | 'supportFive'
              | 'supportSix',
          }),
          {},
        );

        const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const cashTranslation = getTranslations({
          fileName: 'buildModels',
          translationPath: ['cash'],
        }) as TranslatedString;
        const newCashSecurity: Security = {
          financialProduct: {
            id: tempId,
            name: cashLabel,
            isCash: true,
            primaryAssetClass: {
              name: cashLabel,
              id: tempId,
              translatedName: cashTranslation,
            },
            translatedName: cashTranslation,
          },
          percentage: remainingPercentage,
          isCashEquivalent: false,
          taxRank:
            securities.length > 0
              ? Math.max(
                  ...securities.map((security) => security.taxRank ?? 0),
                ) + 1
              : 1,
          id: tempId,
          color: newColorMap[cashLabel],
        };

        const updatedSecurities = [...securities, newCashSecurity];
        onReorderSecurities(updatedSecurities);
      }
    }

    setUtilizeRemainingAllocation(false);
  };

  const handleContinue = () => {
    sendAnalytic(
      ovAnalyticsEvents.workflowsAddModelPortfolioDetailsContinueButtonSelect,
      {
        workflowStepTitle: options?.title,
        workflowId: workflowCompletion?.workflow?.id,
        workflowName: workflowCompletion?.workflow?.name,
        activeWorkflowCompletionId: workflowCompletion?.id,
        objectId: workflowCompletion?.objectId,
        objectType: workflowCompletion?.objectType,
      },
    );

    sendAnalytic(ovAnalyticsEvents.modelPageCreateStep4Continue, {
      securitiesCount: securities.length,
      timestamp: new Date().toISOString(),
    });

    continueFunc();
  };

  const isAssetClass = modelPortfolioDetails.strategy === Strategy.ASSET_CLASS;
  const assetClassLevel = modelPortfolioDetails.assetClassLevel;
  const assetClassBranches = useMemo(
    () => modelPortfolioDetails.assetClassBranches ?? [],
    [modelPortfolioDetails.assetClassBranches],
  );

  const { securitiesWithRanks, hasDuplicateTaxRanks, hasGapsOrInvalidStart } =
    useMemo(
      () => getTaxRankValidation(securities, isAssetClass),
      [isAssetClass, securities],
    );

  const { groupedSecurities, unassignedSecurities, hasInvalidGroupTotal } =
    useAssetClassGrouping(
      isAssetClass,
      securitiesWithRanks,
      assetClassBranches,
      assetClassLevel,
    );

  const handleAutoTargetAll = useCallback(() => {
    const indexById = new Map(
      securities.map((s, i) => [s.financialProduct.id, i]),
    );
    const updatedSecurities = [...securities];

    groupedSecurities?.forEach(({ securities: groupSecs }) => {
      const targets = computeAutoTarget(groupSecs.length);
      groupSecs.forEach((security, groupIdx) => {
        const globalIndex = indexById.get(security.financialProduct.id);
        if (globalIndex == null) return;
        updatedSecurities[globalIndex] = {
          ...updatedSecurities[globalIndex],
          percentage: targets[groupIdx],
        };
      });
    });

    const securitiesWithPreservedTaxRanks = updatedSecurities.map((s, i) => ({
      ...s,
      taxRank: securities[i]?.taxRank,
    }));
    onReorderSecurities(securitiesWithPreservedTaxRanks);
  }, [securities, groupedSecurities, onReorderSecurities]);

  const hasNoSecurity = useMemo(() => securities.length === 0, [securities]);
  const hasNoFinancialProduct = useMemo(
    () =>
      securities.some(
        (security) =>
          !security.financialProduct || !security.financialProduct.id,
      ),
    [securities],
  );
  const hasNoPercentage = useMemo(
    () =>
      securities.some(
        (security) =>
          security.percentage === undefined ||
          security.percentage === null ||
          security.percentage === 0,
      ),
    [securities],
  );
  const invalidPrimaryAssetClass = useMemo(
    () =>
      !isAssetClass &&
      securities.some(
        (security) => !security.financialProduct.primaryAssetClass,
      ),
    [isAssetClass, securities],
  );
  const isTotalPercentageInvalid = useMemo(
    () => !isAssetClass && getRoundedTotalPercentage() > 100,
    [isAssetClass, getRoundedTotalPercentage],
  );

  /**
   * Whether the step is somewhere holdings can be added at all. An asset class
   * model is always laid out in its groups, whatever the table is set to.
   */
  const tableShown =
    !!options?.showSecurityAllocationTable?.enabled || isAssetClass;

  /**
   * Whether the step can be held back over having none. Insisting on holdings
   * a step offers nowhere to add refuses to continue over something the
   * advisor cannot answer.
   */
  const holdingsExpected =
    tableShown &&
    options?.showSecurityAllocationTable?.required !==
      FieldRequiredType.NOT_REQUIRED;

  const validateAssetClass = useCallback((): boolean => {
    setIsEditingSubmitted(true);
    if (hasNoPercentage) return false;
    if (hasDuplicateTaxRanks || hasGapsOrInvalidStart) return false;
    return validateGroupAllocations(
      groupedSecurities ?? [],
      unassignedSecurities.length,
    );
  }, [
    unassignedSecurities,
    hasNoPercentage,
    hasDuplicateTaxRanks,
    hasGapsOrInvalidStart,
    groupedSecurities,
  ]);

  const isDisabled = useMemo(
    () =>
      importLoading ||
      hasNoPercentage ||
      (isAssetClass &&
        (unassignedSecurities.length > 0 || hasInvalidGroupTotal)),
    [
      importLoading,
      hasNoPercentage,
      isAssetClass,
      unassignedSecurities.length,
      hasInvalidGroupTotal,
    ],
  );

  const isSaveForLaterDisabled = useMemo(
    () =>
      isDisabled ||
      (isAssetClass && (hasDuplicateTaxRanks || hasGapsOrInvalidStart)),
    [hasDuplicateTaxRanks, hasGapsOrInvalidStart, isAssetClass, isDisabled],
  );

  return (
    <Box sx={{ ...options?.componentProps, mt: 3, mr: -30 }}>
      {isAssetClass && unassignedSecurities.length > 0 && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {t('modelPortfolios:portfolio.unassignedSecuritiesError')}
        </Alert>
      )}

      {isAssetClass &&
        unassignedSecurities.length === 0 &&
        hasInvalidGroupTotal && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {t('modelPortfolios:portfolio.groupAllocationError')}
          </Alert>
        )}

      {isEditingSubmitted && hasDuplicateTaxRanks && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {t('modelPortfolios:portfolio.duplicateTaxRankError')}
        </Alert>
      )}

      {isEditingSubmitted && !hasDuplicateTaxRanks && hasGapsOrInvalidStart && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {t('modelPortfolios:portfolio.taxRankGapsError')}
        </Alert>
      )}

      {isEditingSubmitted && hasNoPercentage && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {t('modelPortfolios:portfolio.zeroPercentError')}
        </Alert>
      )}

      {isEditingSubmitted &&
        !isAssetClass &&
        ((hasNoSecurity && holdingsExpected) ||
          hasNoFinancialProduct ||
          invalidPrimaryAssetClass) && (
          <Stack
            spacing={1}
            sx={{
              mb: 2,
            }}
          >
            {hasNoSecurity && holdingsExpected && (
              <Alert severity='error'>
                {t('modelPortfolios:portfolio.noSecurityError')}
              </Alert>
            )}
            {hasNoFinancialProduct && (
              <Alert severity='error'>
                {t('modelPortfolios:portfolio.noFinancialProductError')}
              </Alert>
            )}
            {invalidPrimaryAssetClass && (
              <Alert severity='error'>
                {t('modelPortfolios:portfolio.invalidPrimaryAssetClass')}
              </Alert>
            )}
          </Stack>
        )}

      <WorkflowCompletionForm
        onSubmit={submit}
        submitButtonProps={{
          label: t('shared:continue'),
          disabled: isDisabled,
          dataTestId: 'add-model-portfolio-securities-continue-button',
        }}
        buttonFooter={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'flex-end',
              flex: 1,
            }}
          >
            {options?.buttonSaveForLater?.enabled && (
              <Button
                sx={{ mr: 2, textAlign: 'center' }}
                data-testid='add-model-portfolio-securities-save-for-later-button'
                variant='contained'
                onClick={() => {
                  setIsEditingSubmitted(true);
                  if (
                    (hasNoSecurity && holdingsExpected) ||
                    hasNoPercentage ||
                    invalidPrimaryAssetClass ||
                    isTotalPercentageInvalid ||
                    (isAssetClass &&
                      (unassignedSecurities.length > 0 ||
                        hasInvalidGroupTotal ||
                        hasDuplicateTaxRanks ||
                        hasGapsOrInvalidStart))
                  ) {
                    return;
                  }

                  sendAnalytic(
                    ovAnalyticsEvents.modelPageCreateStep4SaveLater,
                    {
                      securitiesCount: securities.length,
                      timestamp: new Date().toISOString(),
                    },
                  );

                  onSaveForLater();
                }}
                color='primary'
                disabled={isSaveForLaterDisabled}
                loading={loading}
              >
                {translateBackend(options?.buttonSaveForLater?.label)}
              </Button>
            )}
          </Box>
        }
      >
        {(options?.title || isAssetClass) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: 1,
              mb: 2,
            }}
          >
            {options?.title ? (
              <Typography
                variant='displayLarge'
                sx={{
                  fontWeight: 400,
                  fontSize: '40px',
                  fontFamily: 'Tiempos Text',
                }}
              >
                {translateBackend(options?.title)}
                {additionalInfo(options?.title?.additionalInfo) && (
                  <InfoDialog
                    information={
                      additionalInfo(options?.title?.additionalInfo) ?? ''
                    }
                  />
                )}
              </Typography>
            ) : (
              <Box />
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {isAssetClass && (
                <Button
                  data-testid='auto-target-all'
                  variant='contained'
                  onClick={handleAutoTargetAll}
                  size='small'
                  color='secondary'
                >
                  {t('modelPortfolios:autoTarget')}
                </Button>
              )}
              {/* Holdings imported into a step with no table land out of
                  sight, with nowhere to read them over or put them right. */}
              {tableShown && (
                <Tooltip title={t('modelPortfolios:importCsv.uploadTooltip')}>
                  <IconButton
                    data-testid='add-model-portfolio-securities-import-csv-button'
                    size='xsmall'
                    aria-label='import csv'
                    onClick={() => {
                      sendAnalytic(
                        ovAnalyticsEvents.modalOpen(
                          'import_csv',
                          'model_portfolio_create',
                        ),
                        {
                          timestamp: new Date().toISOString(),
                        },
                      );
                      setShowImportCsvModal(true);
                    }}
                  >
                    <UploadFileRoundedIcon fontSize='inherit' />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        )}

        {options?.subtitle && (
          <Typography variant='bodyLarge' sx={{ mb: 2 }}>
            <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
              {translateBackend(options.subtitle)}
            </ReactMarkdown>
          </Typography>
        )}

        <Grid container spacing={2}>
          {options?.securityAllocationChart?.enabled && (
            <Grid size={{ xs: 12, md: 3.5 }}>
              <Card
                variant='outlined'
                sx={{ borderColor: 'rgba(0, 0, 0, 0.12)' }}
              >
                <CardContent>
                  <PieChartSecurities
                    testId='pie-chart-securities'
                    securities={isAssetClass ? [] : securities}
                    modelPortfolioName={
                      modelPortfolioDetails?.translatedName?.[
                        getBackendLanguage()
                      ] ?? ''
                    }
                    portfolioTemplate={
                      isAssetClass && assetClassBranches.length > 0
                        ? ({
                            ...portfolioTemplate,
                            assetClassBranches,
                          } as PortfolioTemplate)
                        : portfolioTemplate
                    }
                    assetClassLevel={isAssetClass ? assetClassLevel : undefined}
                  />
                </CardContent>
              </Card>
            </Grid>
          )}

          <Grid size={{ xs: 12, md: 8.5 }}>
            {options?.showSecurityAllocationTable?.enabled && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 2,
                }}
              >
                <InfoOutlinedIcon
                  fontSize='small'
                  data-testid={'info-taxrank-edit-sercurity-icon'}
                />
                <Typography
                  variant='bodyMedium'
                  data-testid={'info-taxrank-edit-sercurity-text'}
                  sx={{ fontWeight: 400, whiteSpace: 'nowrap' }}
                >
                  {t('modelPortfolios:portfolio.securitiesOrderedByTaxRank')}
                </Typography>
              </Box>
            )}
            {/* An admin who turned the table off asked for no table, not for
                an empty card where one would have been. */}
            {(options?.showSecurityAllocationTable?.enabled ||
              isAssetClass) && (
              <Card
                variant='outlined'
                sx={{ borderColor: 'rgba(0, 0, 0, 0.12)' }}
              >
                <SecuritiesSelectionTable
                  securities={securitiesWithRanks}
                  options={options}
                  submitAttempted={isEditingSubmitted}
                  onRemoveSecurity={(index: number) => onRemoveSecurity(index)}
                  onUpdateSecurity={(index: number, security: Security) =>
                    onUpdateSecurity(index, security)
                  }
                  onAddSecurity={() => {
                    sendAnalytic(
                      ovAnalyticsEvents.modelPageCreateStep4AddSecurity,
                      {
                        currentSecuritiesCount: securities.length,
                        timestamp: new Date().toISOString(),
                      },
                    );
                    onAddSecurity();
                  }}
                  onAddCash={isAssetClass ? onAddCash : undefined}
                  onUpdateTaxRank={(index: number, value: number) =>
                    onUpdateTaxRank(index, value)
                  }
                  onUpdateTargetPercentage={(index: number, value: number) =>
                    onUpdateTargetPercentage(index, value)
                  }
                  onReorderSecurities={(data: any[]) =>
                    onReorderSecurities(data)
                  }
                  groupedByAssetClass={
                    isAssetClass && groupedSecurities
                      ? groupedSecurities
                      : undefined
                  }
                  unassignedSecurities={
                    isAssetClass ? unassignedSecurities : undefined
                  }
                  securitySelectAssetClassLevel={
                    isAssetClass ? assetClassLevel : undefined
                  }
                />
              </Card>
            )}
          </Grid>
        </Grid>
      </WorkflowCompletionForm>

      <ImportCsvPromptDialog
        open={showImportPrompt && tableShown}
        onClose={() => {
          onCloseImportPrompt?.();
        }}
        onImport={() => setShowImportCsvModal(true)}
      />

      <ImportCsvModal
        open={showImportCsvModal}
        onClose={() => {
          sendAnalytic(
            ovAnalyticsEvents.modalClose(
              'import_csv',
              'model_portfolio_create',
            ),
            {
              timestamp: new Date().toISOString(),
            },
          );
          setShowImportCsvModal(false);
        }}
        onImport={async (file, onProgress) => {
          await onImportFromCsv(file);
          onProgress?.(100);
          sendAnalytic(
            ovAnalyticsEvents.modalSubmit(
              'import_csv',
              'model_portfolio_create',
            ),
            {
              securitiesCount: securities.length,
              timestamp: new Date().toISOString(),
            },
          );
        }}
        testIdPrefix='add-model-portfolio-securities'
        importFileType='PORTFOLIO_SECURITIES'
        title={t('modelPortfolios:importCsv.modalTitle')}
        description={t('modelPortfolios:importCsv.modalDescription')}
        importButtonLabel={t('modelPortfolios:importCsv.import')}
        successMessage={t('modelPortfolios:importCsv.successMessage')}
        errorMessage={t('modelPortfolios:importCsv.errorMessage')}
      />

      <ValidateSecuritiesDialogBox
        open={isAllocationExceededDialogOpen}
        onClose={() => setIsAllocationExceededDialogOpen(false)}
        title={t('modelPortfolios:portfolio.exceedsTotalAllocation')}
        bodyText={t('modelPortfolios:portfolio.totalAllocationError', {
          totalPercentage: `<span style="color: #FF0000; font-weight: bold">${getRoundedTotalPercentage()}%</span>`,
          maxPercentage:
            '<span style="color: #000000; font-weight: bold">100%</span>',
        })}
        confirmButtonText={t('modelPortfolios:portfolio.ok')}
        onConfirm={() => setIsAllocationExceededDialogOpen(false)}
        disableCancelButton
        isChecked={true}
      />

      {options?.validateSecurities?.enabled && (
        <ValidateSecuritiesDialogBox
          open={isDialogOpen}
          onClose={() => {
            setIsDialogOpen(false);
            setUtilizeRemainingAllocation(false);
          }}
          title={translateBackend(options?.validateSecurities?.title)}
          bodyText={translateBackend(options?.validateSecurities?.bodyText)}
          checkboxLabel={translateBackend(
            options?.validateSecurities?.checkboxLabel,
          )}
          onCheckboxChange={(checked) => setUtilizeRemainingAllocation(checked)}
          confirmButtonText={t('shared:confirm')}
          cancelButtonText={t('shared:cancel')}
          onConfirm={() => {
            setIsDialogOpen(false);
            adjustSecuritiesForCashAndEquivalents();
          }}
          isChecked={utilizeRemainingAllocation}
        />
      )}

      <ConfirmationModal
        open={isTemplateValidationDialogOpen}
        onConfirm={() => {
          sendAnalytic(
            ovAnalyticsEvents.modelPageCreateStep4DoesntMatchModalContinue,
            {
              timestamp: new Date().toISOString(),
            },
          );
          setIsTemplateValidationDialogOpen(false);
          handleContinue();
        }}
        onCancel={() => {
          sendAnalytic(
            ovAnalyticsEvents.modelPageCreateStep4DoesntMatchModalCancel,
            {
              timestamp: new Date().toISOString(),
            },
          );
          setIsTemplateValidationDialogOpen(false);
        }}
        title={t('modelPortfolios:templateDoesntMatch.title')}
        bodyText={t('modelPortfolios:templateDoesntMatch.body')}
        confirmButtonLabel={t('shared:continue')}
        cancelButtonLabel={t('shared:cancel')}
        confirmButtonColor='primary'
        cancelButtonColor='primary'
        testIdPrefix='template-validation-dialog'
      />

      {showReviewDialog && onReviewSubmit && onReviewClose && (
        <SecuritiesReviewDialog
          open={showReviewDialog}
          onClose={onReviewClose}
          onSubmit={onReviewSubmit}
          unrecognizedSecurities={unrecognizedSecurities}
          availableSecurities={availableSecurities}
          loading={loading}
        />
      )}
    </Box>
  );
};

export default AddModelPortfolioSecuritiesVisual;
